import moment from 'moment'

import { DocumentsToList } from '../@types/SimulationSummaryDownload'
import { AddressDetails } from '../@types/ValidateZipCode'
import { AnimalsSecured, Question } from '../Types/AnimalStore'
import { POLICY_CODE } from '../constants/policyCode'

export const initialValidationQuestionary = (
  _animalsList: AnimalsSecured[],
) => {
  let answered = {}
  const getQuestionMOR = (questions: Question[]) =>
    questions?.find((question) => (question.conditions || [])?.length > 0)

  const getQuestionPM = (questions: Question[]) =>
    questions?.find((question) =>
      getQuestionMOR(questions)
        ?.conditions?.map((item) => item.affectedQuestionCode)
        .includes(question.id as string),
    )

  _animalsList.forEach((_animal) => {
    _animal.questionary?.forEach((declaration) => {
      declaration.questions.forEach((item) => {
        if (item.id === getQuestionPM(declaration.questions)?.id) return

        answered = {
          ...answered,
          [`${_animal.id}-${declaration.id}-${item.id}`]: item.answer,
        }
      })
    })
  })

  return answered
}

export function validateObjectValues(obj: Record<string, any>): boolean {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (obj[key] === null || obj[key] === '') {
        return false
      }
    }
  }
  return true
}

export const isAnimalOlderThan = (animal: AnimalsSecured, days: number) => {
  const TODAY = new Date(moment().format('YYYY-MM-DD'))
  const birthDate = new Date(animal.birthDate!)
  const timeDifferenceMs = TODAY.getTime() - birthDate.getTime()
  const daysDifference = timeDifferenceMs / (1000 * 60 * 60 * 24)
  return daysDifference >= days
}

type ObjectProps = {
  [key: string]: boolean
}

export function removePropertiesImmutably(
  obj: ObjectProps,
  propertiesToRemove: string[],
): ObjectProps {
  const newObj: ObjectProps = {}

  for (const key in obj) {
    if (obj.hasOwnProperty(key) && !propertiesToRemove.includes(key)) {
      newObj[key] = obj[key]
    }
  }

  return newObj
}

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const validatePhoneNumber = (
  phoneNumber: string,
  isMobile: boolean,
): boolean => {
  const initialPrefixes = isMobile ? ['91', '92', '93', '96'] : ['2']
  const validPrefixes = initialPrefixes

  if (phoneNumber.length !== 9) {
    return false
  }

  const prefixMatch = (validPrefixes || [])?.some((prefix) =>
    phoneNumber.startsWith(prefix),
  )

  return prefixMatch
}

export const referenceNumberResumeType = (referenceNumber: string) => {
  const isSimulation =
    referenceNumber.charAt(0).toUpperCase() === POLICY_CODE.simulation
  const isQuotation =
    referenceNumber.charAt(0).toUpperCase() === POLICY_CODE.quotation

  return {
    isSimulation,
    isQuotation,
    isProposal: !isSimulation && !isQuotation,
  }
}

export function removeDuplicatesDocuments(
  documentList: DocumentsToList[],
  key: string,
) {
  const _documents = new Set()

  return documentList.filter((item) => {
    const itemKey = key ? item[key as never] : JSON.stringify(item)
    if (_documents.has(itemKey)) {
      return false
    } else {
      _documents.add(itemKey)
      return true
    }
  })
}

// This function has been taken from ageas components NIF.
export const validateNIF = (nifValue: any) => {
  if (
    (nifValue.substr(0, 1) !== '1' && // pessoa singular
      nifValue.substr(0, 1) !== '2' && // pessoa singular
      nifValue.substr(0, 1) !== '3' && // pessoa singular
      nifValue.substr(0, 2) !== '45' && // pessoa singular não residente
      nifValue.substr(0, 1) !== '5' && // pessoa colectiva
      nifValue.substr(0, 1) !== '6' && // administração pública
      nifValue.substr(0, 2) !== '70' && // herança indivisa
      nifValue.substr(0, 2) !== '74' && // herança indivisa
      nifValue.substr(0, 2) !== '75' && // herança indivisa
      nifValue.substr(0, 2) !== '71' && // pessoa colectiva não residente
      nifValue.substr(0, 2) !== '72' && // fundos de investimento
      nifValue.substr(0, 2) !== '77' && // atribuição oficiosa
      nifValue.substr(0, 2) !== '78' && // Atribuição oficiosa a não residentes abrangidos pelo processo VAT REFUND
      nifValue.substr(0, 2) !== '79' && // regime excepcional
      nifValue.substr(0, 1) !== '8' && // empresário em nome individual (extinto)
      nifValue.substr(0, 2) !== '90' && // condominios e sociedades irregulares
      nifValue.substr(0, 2) !== '91' && // condominios e sociedades irregulares
      nifValue.substr(0, 2) !== '98' && // não residentes
      nifValue.substr(0, 2) !== '99') || // sociedades civis
    nifValue.length !== 9
  ) {
    return false
  } else {
    const total =
      nifValue.substr(0, 1) * 9 +
      nifValue.substr(1, 1) * 8 +
      nifValue.substr(2, 1) * 7 +
      nifValue.substr(3, 1) * 6 +
      nifValue.substr(4, 1) * 5 +
      nifValue.substr(5, 1) * 4 +
      nifValue.substr(6, 1) * 3 +
      nifValue.substr(7, 1) * 2

    let checkDigit = 11 - (total % 11)
    checkDigit = checkDigit >= 10 ? 0 : checkDigit

    return checkDigit.toString() === nifValue.substr(8, 1)
  }
}

export const validateNumberAndLetters = (value: string) => {
  const regex = /^[a-zA-Z0-9]+$/

  return regex.test(value)
}

export const validateFullName = (fullName: string, hasInitialName = false) => {
  if (hasInitialName) {
    return true
  }

  const namePattern = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,} [A-Za-zÀ-ÖØ-öø-ÿ' -]{2,}$/

  return namePattern.test(fullName)
}

export const validateFistNameLength = (fullName: string) => {
  const elementToDelete = 2
  const nameSplitted = fullName.split(' ')

  if (fullName.length < 70) {
    const firstName =
      nameSplitted.length === 2
        ? [nameSplitted[0]]
        : nameSplitted.slice(0, -elementToDelete)

    const lastName =
      nameSplitted.length <= 2
        ? [nameSplitted.at(-1)]
        : JSON.parse(JSON.stringify(nameSplitted.slice(-elementToDelete)))

    if (lastName.join(' ')?.length > 30) {
      return false
    }

    return firstName.join(' ').length <= 50
  }

  return true
}

export const filterAddresses = (data: AddressDetails[], searchText: string) => {
  const removeAccents = (value: string) =>
    value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

  const normalizedSearchText = removeAccents(searchText.toLowerCase())

  return (data || [])?.filter((address) => {
    const normalizedAddress = removeAccents(
      (address?.address || '').toLowerCase(),
    )
    const normalizedTown = removeAccents((address?.town || '').toLowerCase())

    return (
      normalizedAddress.includes(normalizedSearchText) ||
      normalizedTown.includes(normalizedSearchText)
    )
  })
}
